/**
 * Vaccination P0A API client (ADR-0023). Wraps /api/v1/vaccination/* — protocols/doses,
 * screening-checklist templates, screening, structured order, batch-selected administration,
 * immunization history, next-dose (compute-on-read) and batch recall.
 */
import { apiClient } from "./client";

export interface VaxProtocol {
  id: string;
  code: string;
  name: string;
  disease?: string | null;
  vaccineMedicineId: string;
  versionNo: number;
  active: boolean;
}

export interface VaxDose {
  id: string;
  doseNo: number;
  label?: string | null;
  anchorBasis: "SERIES_START" | "PREV_DOSE" | "AGE";
  minIntervalDays?: number | null;
  recommendedIntervalDays?: number | null;
  minAgeDays?: number | null;
  maxAgeDays?: number | null;
  booster: boolean;
}

export interface VaxTemplate {
  id: string;
  code: string;
  versionNo: number;
  title: string;
  sourceRef?: string | null;
  /** Inclusive age window (in days) this checklist is for; null = open-ended. */
  audienceAgeMinDays?: number | null;
  audienceAgeMaxDays?: number | null;
  /** JSON array: [{ no, label, onAbnormal: "DEFER" | "CONTRAINDICATE" }]. */
  items: string;
}

/** One parsed screening checklist item. */
export interface VaxScreeningItem {
  no: number;
  label: string;
  onAbnormal: "DEFER" | "CONTRAINDICATE";
}

export function parseScreeningItems(itemsJson: string | null | undefined): VaxScreeningItem[] {
  if (!itemsJson) return [];
  try {
    const arr = JSON.parse(itemsJson);
    return Array.isArray(arr) ? (arr as VaxScreeningItem[]) : [];
  } catch {
    return [];
  }
}

/** Pick the checklist whose age window contains the patient's age (in days). */
export function pickTemplateForAge(
  templates: VaxTemplate[],
  ageDays: number | null,
): VaxTemplate | null {
  if (templates.length === 0) return null;
  if (ageDays != null) {
    const match = templates.find((t) => {
      const min = t.audienceAgeMinDays ?? 0;
      const max = t.audienceAgeMaxDays ?? Number.MAX_SAFE_INTEGER;
      return ageDays >= min && ageDays <= max;
    });
    if (match) return match;
  }
  return templates[0];
}

/** Derive the suggested conclusion from ticked-abnormal items (mirrors the
 *  server ScreeningConclusionEngine: CONTRAINDICATE > DEFER > ELIGIBLE). */
export function suggestConclusionLocal(
  items: VaxScreeningItem[],
  abnormalNos: Set<number>,
): "ELIGIBLE" | "DEFER" | "CONTRAINDICATED" {
  let defer = false;
  for (const it of items) {
    if (!abnormalNos.has(it.no)) continue;
    if (it.onAbnormal === "CONTRAINDICATE") return "CONTRAINDICATED";
    if (it.onAbnormal === "DEFER") defer = true;
  }
  return defer ? "DEFER" : "ELIGIBLE";
}

/**
 * The lots a dose may actually be drawn from (#18838).
 *
 * <p>Two conditions, and both are clinical rather than cosmetic: the batch must be of the vaccine
 * that was ordered, and it must have stock. Offering a lot of a different vaccine is how the wrong
 * product gets injected; offering an empty one produces a rejection at the counter with a patient
 * already sleeved up.
 *
 * <p>Extracted from `AdministerStep` so the rule can be tested without opening a Radix Select in
 * jsdom. The server enforces both again — {@code VaccineBatchDispenser} rejects a wrong-vaccine or
 * expired batch — so this is the screen being honest about what it offers, not the safety boundary.
 *
 * <p><b>Expiry is deliberately not filtered here.</b> The current screen shows the expiry date and
 * lets the server refuse; characterising that as-is rather than "fixing" it, because silently
 * hiding an expired lot would change what staff see without anyone deciding to.
 */
export function administrableBatches<
  T extends { medicineId: string; quantityOnHand: number },
>(stock: T[], vaccineMedicineId: string): T[] {
  return stock.filter((r) => r.medicineId === vaccineMedicineId && r.quantityOnHand > 0);
}

/**
 * Whether "Xác nhận tiêm" may be pressed (#18838).
 *
 * <p>A lot must be chosen explicitly. There is no "pick one for me" default: which physical vial
 * went into which arm is the whole of vaccine traceability, and a system that guesses it produces a
 * record nobody can trust during a recall.
 */
export function canAdminister(batchId: string | null | undefined, pending: boolean): boolean {
  return !pending && !!batchId;
}

/**
 * Whether screening lets the visit proceed to a dose (#18838).
 *
 * <p>Only ELIGIBLE proceeds. DEFER, CONTRAINDICATED and REFER all stop here and are recorded as a
 * deferral instead — the rule that stands between a patient who should be postponed and a needle.
 *
 * <p>Mirrors the server, which refuses to create an order from a non-ELIGIBLE screening. Written
 * out as a named function because a bare `=== "ELIGIBLE"` inside a mutation callback is a clinical
 * gate that looks like a string comparison.
 */
export function mayProceedToAdministration(
  conclusion: "ELIGIBLE" | "DEFER" | "CONTRAINDICATED" | "REFER",
): boolean {
  return conclusion === "ELIGIBLE";
}

export interface VaxRecord {
  id: string;
  patientId: string;
  protocolId?: string | null;
  vaccineMedicineId: string;
  doseNo: number;
  batchId?: string | null;
  lotNumber?: string | null;
  expiryDate?: string | null;
  quantity: number;
  route?: string | null;
  site?: string | null;
  administeredAt: string;
  source: string;
  /** #18654: vaccine (medicine) name, joined on the patient-history read; null if unresolved. */
  vaccineName?: string | null;
}

export interface VaxRecallRow {
  patientId: string;
  patientName?: string | null;
  patientCode?: string | null;
  recordId: string;
  administeredAt: string;
  vaccineMedicineId: string;
  batchId: string;
  lotNumber?: string | null;
  doseNo: number;
}

export interface VaxNextDose {
  doseNo: number | null;
  earliest: string | null;
  recommended: string | null;
  status: string | null;
  reason: string;
  seriesComplete: boolean;
}

export interface CreateProtocolReq {
  code: string;
  name: string;
  disease?: string | null;
  vaccineMedicineId: string;
  sourceRef?: string | null;
}

export interface CreateDoseReq {
  doseNo: number;
  label?: string | null;
  anchorBasis: "SERIES_START" | "PREV_DOSE" | "AGE";
  minIntervalDays?: number | null;
  recommendedIntervalDays?: number | null;
  minAgeDays?: number | null;
  maxAgeDays?: number | null;
  booster: boolean;
}

export interface CreateScreeningReq {
  patientId: string;
  protocolId?: string | null;
  templateId?: string | null;
  headerJson?: string | null;
  answersJson?: string | null;
  conclusion: "ELIGIBLE" | "DEFER" | "CONTRAINDICATED" | "REFER";
  referReason?: string | null;
}

export interface CreateOrderReq {
  screeningId: string;
  protocolId: string;
  doseNo: number;
  route?: string | null;
  /** #20019: the operator confirmed a "Mũi số" that differs from the patient's history. */
  doseNoOverride?: boolean;
  doseNoOverrideReason?: string | null;
}

export interface AdministerReq {
  batchId: string;
  quantity?: number | null;
  route?: string | null;
  site?: string | null;
  /** #20016: the operator confirmed giving this dose before the protocol's earliest date. */
  earlyOverride?: boolean;
  earlyOverrideReason?: string | null;
}

const BASE = "/v1/vaccination";

export const vaccinationApi = {
  listProtocols(): Promise<VaxProtocol[]> {
    return apiClient.get<VaxProtocol[]>(`${BASE}/protocols`).then((r) => r.data);
  },
  createProtocol(req: CreateProtocolReq): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/protocols`, req).then((r) => r.data);
  },
  listDoses(protocolId: string): Promise<VaxDose[]> {
    return apiClient.get<VaxDose[]>(`${BASE}/protocols/${protocolId}/doses`).then((r) => r.data);
  },
  addDose(protocolId: string, req: CreateDoseReq): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>(`${BASE}/protocols/${protocolId}/doses`, req)
      .then((r) => r.data);
  },
  listTemplates(): Promise<VaxTemplate[]> {
    return apiClient.get<VaxTemplate[]>(`${BASE}/templates`).then((r) => r.data);
  },
  createTemplate(req: {
    code: string;
    versionNo: number;
    title: string;
    sourceRef?: string | null;
    audienceAgeMinDays?: number | null;
    audienceAgeMaxDays?: number | null;
    itemsJson?: string | null;
  }): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/templates`, req).then((r) => r.data);
  },
  createScreening(req: CreateScreeningReq): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/screenings`, req).then((r) => r.data);
  },
  createOrder(req: CreateOrderReq): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/orders`, req).then((r) => r.data);
  },
  administer(orderId: string, req: AdministerReq): Promise<VaxRecord> {
    return apiClient
      .post<VaxRecord>(`${BASE}/orders/${orderId}/administer`, req)
      .then((r) => r.data);
  },
  history(patientId: string): Promise<VaxRecord[]> {
    return apiClient
      .get<VaxRecord[]>(`${BASE}/patients/${patientId}/records`)
      .then((r) => r.data);
  },
  nextDue(patientId: string, protocolId: string): Promise<VaxNextDose> {
    return apiClient
      .get<VaxNextDose>(`${BASE}/patients/${patientId}/next-due`, { params: { protocolId } })
      .then((r) => r.data);
  },
  recall(batchId: string): Promise<VaxRecallRow[]> {
    return apiClient
      .get<VaxRecallRow[]>(`${BASE}/batch-recall`, { params: { batchId } })
      .then((r) => r.data);
  },

  // ---- P0B ----
  suggestConclusion(templateId: string, abnormalItemNos: number[]): Promise<{ conclusion: string }> {
    return apiClient
      .post<{ conclusion: string }>(`${BASE}/screenings/suggest`, { templateId, abnormalItemNos })
      .then((r) => r.data);
  },
  recordDeferral(req: {
    screeningId?: string | null;
    patientId: string;
    protocolId?: string | null;
    doseNo?: number | null;
    kind?: string | null;
    reason?: string | null;
    reevaluateDate?: string | null;
  }): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/deferrals`, req).then((r) => r.data);
  },
  startObservation(recordId: string, monitorMinutes = 30): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>(`${BASE}/observations`, { recordId, monitorMinutes })
      .then((r) => r.data);
  },
  completeObservation(
    id: string,
    body: { reaction?: string; notes?: string; conclusion?: string; assessed?: boolean },
  ): Promise<void> {
    return apiClient.post(`${BASE}/observations/${id}/complete`, body).then(() => undefined);
  },
  observationBoard(): Promise<VaxObservation[]> {
    return apiClient.get<VaxObservation[]>(`${BASE}/observations/board`).then((r) => r.data);
  },
  reportAdverse(req: {
    patientId: string;
    recordId?: string | null;
    vaccineMedicineId?: string | null;
    batchId?: string | null;
    doseNo?: number | null;
    severity: string;
    symptoms?: string | null;
    onsetAt?: string | null;
    outcome?: string | null;
  }): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/adverse-events`, req).then((r) => r.data);
  },
  createInvoice(recordId: string): Promise<{ id: string; code: string; subtotal: number }> {
    return apiClient
      .post<{ id: string; code: string; subtotal: number }>(`${BASE}/records/${recordId}/invoice`, {})
      .then((r) => r.data);
  },

  // ---- Staff-side vaccination bookings (đặt lịch tiêm) ----
  listBookings(params: { date?: string; status?: string; q?: string }): Promise<VaxBooking[]> {
    return apiClient.get<VaxBooking[]>(`${BASE}/requests`, { params }).then((r) => r.data);
  },
  createBooking(req: CreateBookingReq): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${BASE}/requests`, req).then((r) => r.data);
  },
  confirmBooking(id: string): Promise<void> {
    return apiClient.post(`${BASE}/requests/${id}/confirm`).then(() => undefined);
  },
  cancelBooking(id: string): Promise<void> {
    return apiClient.post(`${BASE}/requests/${id}/cancel`).then(() => undefined);
  },
  fulfillBooking(id: string): Promise<void> {
    return apiClient.post(`${BASE}/requests/${id}/fulfill`).then(() => undefined);
  },
};

export interface VaxBooking {
  id: string;
  patientId: string;
  patientName: string;
  patientCode: string | null;
  patientPhone: string | null;
  protocolId: string | null;
  protocolName: string | null;
  vaccineMedicineId: string | null;
  vaccineName: string | null;
  preferredDate: string | null;
  status: "REQUESTED" | "APPROVED" | "SCHEDULED" | "CANCELLED" | "FULFILLED";
  note: string | null;
  createdAt: string;
  /** Mã đặt lịch người đọc được (LT-XXXXXXXX) — lễ tân đọc cho bệnh nhân, tin nhắc tiêm gửi kèm. */
  bookingCode: string;
}

export interface CreateBookingReq {
  patientId: string;
  protocolId: string;
  preferredDate?: string | null;
  note?: string | null;
}

export const vaxBookingStatusLabel: Record<string, string> = {
  REQUESTED: "Chờ xác nhận",
  APPROVED: "Đã duyệt",
  SCHEDULED: "Đã xếp lịch",
  CANCELLED: "Đã huỷ",
  FULFILLED: "Đã tiêm",
};

export interface VaxObservation {
  id: string;
  recordId: string;
  patientId: string;
  patientName?: string | null;
  patientCode?: string | null;
  patientBirthYear?: number | null;
  startedAt: string;
  monitorMinutes: number;
  reaction: string;
  notes?: string | null;
  conclusion?: string | null;
  clearedAt?: string | null;
}

export const vaccinationQueryKeys = {
  protocols: () => ["vaccination", "protocols"] as const,
  doses: (protocolId: string) => ["vaccination", "doses", protocolId] as const,
  templates: () => ["vaccination", "templates"] as const,
  history: (patientId: string) => ["vaccination", "history", patientId] as const,
  nextDue: (patientId: string, protocolId: string) =>
    ["vaccination", "next-due", patientId, protocolId] as const,
};

export const ROUTE_OPTIONS: { code: string; label: string }[] = [
  { code: "INJECTION_IM", label: "Tiêm bắp" },
  { code: "INJECTION_SC", label: "Tiêm dưới da" },
  { code: "INJECTION_ID", label: "Tiêm trong da" },
  { code: "ORAL", label: "Uống" },
];

export const vaxConclusionLabel: Record<string, string> = {
  ELIGIBLE: "Đủ điều kiện",
  DEFER: "Tạm hoãn",
  CONTRAINDICATED: "Chống chỉ định",
  REFER: "Chuyển tuyến",
};

export const vaxDueStatusLabel: Record<string, string> = {
  UPCOMING: "Sắp tới",
  DUE: "Đến hạn",
  OVERDUE: "Quá hạn",
  PENDING_PREV: "Chờ mũi trước",
};
